import moment from "moment";
import { FormattedMessage } from "react-intl";
import { Column } from "react-table";
import { Constants } from "../../../../constants";
import ActiveIndicator from "../../../Shared/ActiveIndicator/ActiveIndicator";
import HighlightText from "../../../Shared/HighlightText/HighlightText";
import NoDataIndicator from "../../../Shared/NoDataIndicator/NoDataIndicator";
import TableColumnHeader from "../../../Shared/Table/TableColumnHeader/TableColumnHeader";
import PendingUserIcon from "../../PendingUserIcon/PendingUserIcon";
import { PatientsTableItem } from "./ManagePatientsTable";

const managePatientsTableColumns = (
  highlightText?: string
): Column<PatientsTableItem>[] => [
  {
    accessor: "entity",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage id="MANAGEMENT.ENTITY" defaultMessage="Entity" />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) => (
      <HighlightText
        text={row.original?.entity ?? ""}
        highlightText={[highlightText ?? ""]}
      />
    ),
  },
  {
    accessor: "prescribingPhysician",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage
            id="PATIENT.PRESCRIBING_PHYSICIAN"
            defaultMessage="Physician"
          />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) =>
      !row.original?.prescribingPhysician ? (
        <NoDataIndicator />
      ) : (
        <HighlightText
          text={row.original?.prescribingPhysician ?? ""}
          highlightText={[highlightText ?? ""]}
        />
      ),
  },
  {
    accessor: "firstName",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage id="USER.FIRST_NAME" defaultMessage="First Name" />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) => (
      <HighlightText
        text={row.original?.firstName}
        highlightText={[highlightText ?? ""]}
      />
    ),
  },
  {
    accessor: "lastName",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage id="USER.LAST_NAME" defaultMessage="Last Name" />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) => (
      <HighlightText
        text={row.original?.lastName}
        highlightText={[highlightText ?? ""]}
      />
    ),
  },
  {
    accessor: "dateOfBirth",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage id="PATIENT.ECG.DOB" defaultMessage="DOB" />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) =>
      !row.original?.dateOfBirth ? (
        <NoDataIndicator />
      ) : (
        <HighlightText
          text={moment(row.original?.dateOfBirth).format(Constants.DATE_FORMAT)}
          highlightText={[highlightText ?? ""]}
        />
      ),
  },
  {
    accessor: "gender",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage id="USER.GENDER" defaultMessage="Gender" />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) =>
      !row.original?.gender ? (
        <NoDataIndicator />
      ) : (
        <HighlightText
          text={row.original?.gender ?? ""}
          highlightText={[highlightText ?? ""]}
        />
      ),
  },
  {
    accessor: "phone",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage id="USER.PHONE" defaultMessage="Phone" />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) =>
      !row.original?.phone ? (
        <NoDataIndicator />
      ) : (
        <HighlightText
          text={row.original?.phone ?? ""}
          highlightText={[highlightText ?? ""]}
        />
      ),
  },
  {
    accessor: "lastEcgDate",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage
            id="PATIENT.LAST_ECG_DATE"
            defaultMessage="Last ECG Date"
          />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) =>
      !row.original?.lastEcgDate ? (
        <NoDataIndicator />
      ) : (
        <HighlightText
          text={moment(row.original?.lastEcgDate).format(Constants.DATE_FORMAT)}
          highlightText={[highlightText ?? ""]}
        />
      ),
  },
  {
    accessor: "deviceSN",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage
            id="SMARTHEART_SN"
            defaultMessage="Smartheart S\N"
          />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) =>
      !row.original?.deviceSN ? (
        <NoDataIndicator />
      ) : (
        <HighlightText
          text={row.original?.deviceSN ?? ""}
          highlightText={[highlightText ?? ""]}
        />
      ),
  },
  {
    accessor: "creationDate",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage
            id="USER.CREATION_DATE"
            defaultMessage="Creation Date"
          />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) =>
      !row.original?.creationDate ? (
        <NoDataIndicator />
      ) : (
        <HighlightText
          text={moment(row.original?.creationDate).format(
            Constants.DATE_FORMAT
          )}
          highlightText={[highlightText ?? ""]}
        />
      ),
    width: "1%",
  },
  {
    accessor: "status",
    Header: ({ column }) => (
      <TableColumnHeader
        sortable
        isSorted={column.isSorted}
        isSortedDesc={column.isSortedDesc}
      >
        <span className="capitalized">
          <FormattedMessage id="USER.STATUS" defaultMessage="Status" />
        </span>
      </TableColumnHeader>
    ),
    Cell: ({ row }) => {
      switch (row.original?.status) {
        case "pending":
          return <PendingUserIcon />;
        default:
          return <ActiveIndicator active={row.original?.status === "active"} />;
      }
    },
  },
];

export default managePatientsTableColumns;
